// R-131-FIN-DECISION-C — pathway LCOE from discounted lifecycle costs / MWh.

import {
  DEFAULT_INFLATION_PCT_PER_YEAR,
  realDiscountRateFraction,
} from '../inflationBasis';
import {
  buildPathwayCashflows,
  derivePathwayTimeline,
  nameplateAnnualMwh,
  pathwayDiscountRateFraction,
  type PathwayCashflow,
} from './pathwayCashflows';
import type { Pathway, PathwayPhase } from './types';

/**
 * Default long-run inflation assumption used to convert the nominal
 * discount rate to a real rate (Fisher equation) for real LCOE.
 *
 * 2.0%/yr — US Federal Reserve long-run PCE inflation target of 2%
 * (FOMC "Statement on Longer-Run Goals and Monetary Policy Strategy",
 * reaffirmed annually). This is the documented default; callers may
 * override via `computePathwayLcoe(pathway, { inflationPct })` for
 * jurisdiction- or scenario-specific assumptions.
 */
export const DEFAULT_INFLATION_PCT = DEFAULT_INFLATION_PCT_PER_YEAR;

/**
 * Thrown by `computePathwayLcoe(pathway, { strict: true })` when a
 * pathway has no cashflow phases, so LCOE cannot be computed. On the
 * default (non-strict) path the function instead returns NaN LCOE
 * values so per-sample callers (e.g. Monte Carlo) can exclude/flag the
 * invalid sample without aborting the whole run.
 */
export class EmptyCashflowError extends Error {
  readonly pathwayId: string;
  constructor(pathwayId: string) {
    super(`Pathway ${pathwayId} has no cashflow phases — cannot compute LCOE`);
    this.name = 'EmptyCashflowError';
    this.pathwayId = pathwayId;
  }
}

export interface PathwayLcoeResult {
  lcoeNominalPerMwhUsd: number;
  lcoeRealPerMwhUsd: number;
  components: {
    capexPv: number;
    opexPv: number;
    fuelPv: number;
    refurbPv: number;
    decommPv: number;
    residualPv: number;
  };
}

function pvStream(
  flows: PathwayCashflow[],
  rate: number,
  selector: (row: PathwayCashflow) => number,
): number {
  let total = 0;
  for (const row of flows) {
    total += selector(row) / Math.pow(1 + rate, row.cohortYear);
  }
  return total;
}


/**
 * PV of the delivered MWh — the LCOE denominator.
 *
 * OPERATING-HORIZON INVARIANT (R-131-FIN-DECISION-C): this half-open window
 * [COD, COD + ppaTermYears) is the SAME one the cashflow engine now uses for
 * opex/fuel and revenue (`buildPathwayCashflows`), so every year in the
 * numerator has energy in the denominator. The engine's opex gate used to run
 * one year LONGER (`year <= codYear + term`), putting a cost-only year into the
 * numerator with zero energy underneath it.
 */
function discountedEnergyMwh(
  pathway: Pathway,
  flows: PathwayCashflow[],
  rate: number,
): number {
  const timeline = derivePathwayTimeline(pathway, new Date(`${flows[0]?.year ?? 2026}-06-01T00:00:00Z`));
  const annualMwh = nameplateAnnualMwh(pathway);
  let total = 0;
  for (const row of flows) {
    const yearsFromCod = row.year - timeline.codYear;
    if (yearsFromCod < 0 || yearsFromCod >= pathway.revenue.ppaTermYears) continue;
    total += annualMwh / Math.pow(1 + rate, row.cohortYear);
  }
  return total;
}

/**
 * LCOE = PV(lifecycle costs) / PV(lifetime MWh) at the pathway's
 * risk-adjusted WACC. Residual value and working-capital recovery
 * reduce the cost numerator via `residualPv`.
 *
 * Options:
 * - `now`: valuation anchor date (default: current date).
 * - `inflationPct`: long-run inflation for the real-rate conversion
 *   (default: {@link DEFAULT_INFLATION_PCT} = 2.0%, US Fed PCE target).
 * - `strict`: when true, a pathway with no cashflow phases throws
 *   {@link EmptyCashflowError} instead of returning NaN. Use on the
 *   deterministic single-pathway path; leave false (default) for
 *   per-sample callers such as Monte Carlo, where a NaN sentinel lets
 *   downstream summaries exclude/flag the bad sample without aborting.
 *
 * Empty-cashflow handling (default, non-strict): returns NaN LCOE and
 * NaN components — a clearly-invalid sentinel — rather than 0, which
 * previously silently masked structurally invalid pathways.
 */
export function computePathwayLcoe(
  pathway: Pathway,
  opts?: { now?: Date; inflationPct?: number; strict?: boolean },
): PathwayLcoeResult {
  const now = opts?.now ?? new Date();
  const inflationPct = opts?.inflationPct ?? DEFAULT_INFLATION_PCT;
  const flows = buildPathwayCashflows(pathway, now);
  if (flows.length === 0) {
    if (opts?.strict) {
      throw new EmptyCashflowError(pathway.id);
    }
    // NaN sentinel (not 0): downstream per-sample summaries can detect
    // and exclude/flag an invalid pathway instead of being skewed by a
    // spurious zero-cost LCOE.
    return {
      lcoeNominalPerMwhUsd: NaN,
      lcoeRealPerMwhUsd: NaN,
      components: {
        capexPv: NaN,
        opexPv: NaN,
        fuelPv: NaN,
        refurbPv: NaN,
        decommPv: NaN,
        residualPv: NaN,
      },
    };
  }

  const nominalRate = pathwayDiscountRateFraction(pathway);
  const realRate = realDiscountRateFraction(nominalRate, inflationPct);

  const capexPv = pvStream(flows, nominalRate, (row) => row.capex);
  const opexPv = pvStream(flows, nominalRate, (row) => row.opex);
  const fuelPv = pvStream(flows, nominalRate, (row) => row.fuel);
  const refurbPv = pvStream(flows, nominalRate, (row) => row.refurbishment);
  const decommPv = pvStream(flows, nominalRate, (row) => row.decommissioning);
  const residualPv = pvStream(flows, nominalRate, (row) => row.residualValue);

  const workingCapitalUsd = pathway.capex.workingCapitalUsd ?? 0;
  const timeline = derivePathwayTimeline(pathway, now);
  const wcRecoveryPv =
    workingCapitalUsd > 0
      ? workingCapitalUsd / Math.pow(1 + nominalRate, timeline.eolYear - timeline.nowYear)
      : 0;

  const totalCostPv =
    capexPv + opexPv + fuelPv + refurbPv + decommPv - residualPv - wcRecoveryPv;
  const energyPv = discountedEnergyMwh(pathway, flows, nominalRate);

  const lcoeNominalPerMwhUsd = energyPv > 0 ? totalCostPv / energyPv : 0;

  const realCapexPv = pvStream(flows, realRate, (row) => row.capex);
  const realOpexPv = pvStream(flows, realRate, (row) => row.opex);
  const realFuelPv = pvStream(flows, realRate, (row) => row.fuel);
  const realRefurbPv = pvStream(flows, realRate, (row) => row.refurbishment);
  const realDecommPv = pvStream(flows, realRate, (row) => row.decommissioning);
  const realResidualPv = pvStream(flows, realRate, (row) => row.residualValue);
  const realWcRecoveryPv =
    workingCapitalUsd > 0
      ? workingCapitalUsd / Math.pow(1 + realRate, timeline.eolYear - timeline.nowYear)
      : 0;
  const realCostPv =
    realCapexPv +
    realOpexPv +
    realFuelPv +
    realRefurbPv +
    realDecommPv -
    realResidualPv -
    realWcRecoveryPv;
  const realEnergyPv = discountedEnergyMwh(pathway, flows, realRate);
  const lcoeRealPerMwhUsd = realEnergyPv > 0 ? realCostPv / realEnergyPv : 0;

  return {
    lcoeNominalPerMwhUsd,
    lcoeRealPerMwhUsd,
    components: {
      capexPv,
      opexPv,
      fuelPv,
      refurbPv,
      decommPv,
      residualPv: residualPv + wcRecoveryPv,
    },
  };
}

// ───────────────────────────────────────────────────────────────────────────
// R-131-FIN-DECISION-PHASE-LCOE — per-phase LCOE attribution.
//
// For multi-phase pathways, attribute the authoritative
// WACC-discounted real LCOE to each phase segment ("bridge-phase" vs
// "grid-phase") so the comparison can show WHERE cost accumulates.
//
// METHOD (no fabricated numbers): the function reuses the EXACT same
// year-by-year cashflow stream (`buildPathwayCashflows`) and the same
// real-rate PV math as `computePathwayLcoe`. It partitions every cashflow
// YEAR into exactly one phase segment, then for each segment computes
//   phaseLcoe = PV(costs in segment) / PV(energy in segment)
// at the same real discount rate. Because the partition is exhaustive and
// disjoint, Σ(PV costsᵢ) === whole-pathway cost PV and Σ(PV energyᵢ) ===
// whole-pathway energy PV, so the energy-PV-weighted blend of per-phase
// LCOEs reconciles EXACTLY to `computePathwayLcoe(...).lcoeRealPerMwhUsd`.
//
// Dimensional analysis (per segment): [USD] / [MWh] = [USD/MWh]; the blended
// lifecycle figure is Σ[USD] / Σ[MWh] = [USD/MWh]. ✓
// ───────────────────────────────────────────────────────────────────────────

/** LCOE attribution for one phase segment of a (possibly multi-phase) pathway. */
export interface PathwayPhaseLcoe {
  /** Stable segment id, e.g. "phase-0". */
  phaseId: string;
  /** Human label, e.g. "Bridge (gas-cc operation)". */
  label: string;
  /** Pathway classification this segment belongs to (carried from the pathway). */
  classification: Pathway['classification'];
  /** Generation source for this segment, when derivable from phase metadata. */
  powerSource?: string;
  /** WACC-discounted real LCOE attributed to this segment, USD/MWh. NaN if no energy. */
  lcoeRealUsdPerMwh: number;
  /** Undiscounted CAPEX outflow within this segment, USD. */
  capexUsd: number;
  /** Number of calendar years spanned by this segment. */
  years: number;
  /** First / last calendar year (inclusive) of this segment. */
  startYear: number;
  endYear: number;
  /** PV of energy attributed to this segment, MWh (blend weight). */
  energyPvMwh: number;
  /** PV of net lifecycle cost attributed to this segment, USD (numerator). */
  costPvUsd: number;
}

export interface PathwayPhaseLcoeBreakdown {
  /** Energy-PV-weighted blend of per-phase LCOEs — reconciles to the whole. */
  blendedLcoeRealUsdPerMwh: number;
  /** The authoritative whole-pathway real LCOE for cross-checking. */
  wholePathwayLcoeRealUsdPerMwh: number;
  phaseBreakdown: PathwayPhaseLcoe[];
}

/**
 * Build disjoint, exhaustive phase SEGMENTS over the cashflow years.
 *
 * A segment groups a construction phase with the operation phase that
 * follows it (the cohort it builds), so a pathway that builds twice yields two
 * segments. NOTE (Q7): no production path currently emits such a pathway — the
 * hybrid-bridge preset was retired and every remaining preset builds exactly
 * one construction phase. This stays GENERAL rather than specialised to N=1,
 * because single-phase is simply the N=1 case of the same partition and
 * per-operation-phase support is the stated condition for restoring a bridge.
 * Years before the first phase or after the last are folded into
 * the nearest segment so the partition stays exhaustive (no orphan years,
 * preserving the reconciliation). Single-phase pathways collapse to one
 * segment spanning all years.
 */
function buildPhaseSegments(
  pathway: Pathway,
  flows: PathwayCashflow[],
): Array<{ id: string; label: string; powerSource?: string; startYear: number; endYear: number }> {
  const minYear = flows[0].year;
  const maxYear = flows[flows.length - 1].year;

  const phasesSorted = [...pathway.phases].sort((a, b) =>
    a.startDate < b.startDate ? -1 : a.startDate > b.startDate ? 1 : 0,
  );
  const operationPhases = phasesSorted.filter((p) => p.kind === 'operation');

  // Single (or zero) operation phase → one segment covering everything. This is
  // the byte-identical single-phase case (one-element breakdown, no behaviour
  // change to the existing whole-pathway LCOE).
  if (operationPhases.length <= 1) {
    return [
      {
        id: 'phase-0',
        label: phaseLabel(pathway, operationPhases[0], 0),
        powerSource: phasePowerSource(operationPhases[0]),
        startYear: minYear,
        endYear: maxYear,
      },
    ];
  }

  // Multi-operation pathway: split at the START YEAR of each operation phase
  // beyond the first. Each cut point opens a new segment; the construction
  // years feeding an operation phase fall into the segment that ends just
  // before that operation begins (its preceding construction), which is the
  // natural "build-then-run" cohort grouping.
  const cutYears = operationPhases
    .slice(1)
    .map((p) => utcYear(p.startDate))
    .filter((y) => y > minYear && y <= maxYear)
    .sort((a, b) => a - b);

  // Each cut year opens a new segment. The construction years feeding an
  // operation phase fall into the segment that ENDS just before that operation
  // begins (its preceding construction), the natural "build-then-run" cohort.
  // Segment k spans [segStart, cutYears[k]-1]; the last spans [segStart, maxYear].
  let segStart = minYear;
  const segments: Array<{ start: number; end: number; op?: PathwayPhase }> = [];
  for (let k = 0; k < cutYears.length; k += 1) {
    segments.push({ start: segStart, end: cutYears[k] - 1, op: operationPhases[k] });
    segStart = cutYears[k];
  }
  segments.push({ start: segStart, end: maxYear, op: operationPhases[operationPhases.length - 1] });

  return segments.map((seg, idx) => ({
    id: `phase-${idx}`,
    label: phaseLabel(pathway, seg.op, idx),
    powerSource: phasePowerSource(seg.op),
    startYear: seg.start,
    endYear: seg.end,
  }));
}

function utcYear(isoDate: string): number {
  return new Date(`${isoDate}T00:00:00Z`).getUTCFullYear();
}

function phasePowerSource(phase: PathwayPhase | undefined): string | undefined {
  if (!phase) return undefined;
  if (phase.sourceMix && phase.sourceMix.length > 0) {
    return phase.sourceMix.map((c) => c.powerSource).join('+');
  }
  return undefined;
}

function phaseLabel(pathway: Pathway, phase: PathwayPhase | undefined, idx: number): string {
  const source = phasePowerSource(phase);
  if (source) return `Phase ${idx + 1} (${source})`;
  // A hybrid-classified pathway (only persisted ones since Q7) names its
  // segments by position; single-phase uses the pathway technology so the
  // one-element breakdown is self-describing.
  if (pathway.classification === 'hybrid') {
    return idx === 0 ? 'Bridge phase' : 'Grid phase';
  }
  return `Phase ${idx + 1} (${pathway.technology})`;
}

/**
 * Attribute the whole-pathway real LCOE to per-phase segments.
 *
 * Reuses `buildPathwayCashflows` + the same real-rate PV math as
 * `computePathwayLcoe`, so the energy-PV-weighted blend of the returned
 * per-phase LCOEs equals `computePathwayLcoe(...).lcoeRealPerMwhUsd` to within
 * floating-point tolerance. Single-phase pathways return a one-element
 * breakdown (no behaviour change to the whole-pathway figure).
 *
 * Empty-cashflow pathways return NaN LCOEs and an empty breakdown — the same
 * NaN sentinel convention as `computePathwayLcoe` (non-strict).
 */
export function computePathwayPhaseLcoeBreakdown(
  pathway: Pathway,
  opts?: { now?: Date; inflationPct?: number },
): PathwayPhaseLcoeBreakdown {
  const now = opts?.now ?? new Date();
  const inflationPct = opts?.inflationPct ?? DEFAULT_INFLATION_PCT;
  const flows = buildPathwayCashflows(pathway, now);
  const whole = computePathwayLcoe(pathway, { now, inflationPct });

  if (flows.length === 0) {
    return {
      blendedLcoeRealUsdPerMwh: NaN,
      wholePathwayLcoeRealUsdPerMwh: NaN,
      phaseBreakdown: [],
    };
  }

  const nominalRate = pathwayDiscountRateFraction(pathway);
  const realRate = realDiscountRateFraction(nominalRate, inflationPct);

  const timeline = derivePathwayTimeline(pathway, now);
  const annualMwh = nameplateAnnualMwh(pathway);
  const workingCapitalUsd = pathway.capex.workingCapitalUsd ?? 0;

  // Per-YEAR cost and energy contributions at the real rate, matching the
  // whole-pathway numerator/denominator term-for-term:
  //   cost_t  = (capex + opex + fuel + refurb + decomm − residual)_t / (1+r)^cohort
  //             − (WC recovery booked at EOL, discounted)
  //   energy_t = annualMwh / (1+r)^cohort  for COD ≤ t < COD + ppaTerm, else 0
  const wcRecoveryYear = timeline.eolYear;
  function yearCostPv(row: PathwayCashflow): number {
    const df = Math.pow(1 + realRate, row.cohortYear);
    const grossCost =
      row.capex + row.opex + row.fuel + row.refurbishment + row.decommissioning - row.residualValue;
    let pv = grossCost / df;
    if (workingCapitalUsd > 0 && row.year === wcRecoveryYear) {
      pv -= workingCapitalUsd / df;
    }
    return pv;
  }
  function yearEnergyPv(row: PathwayCashflow): number {
    const yearsFromCod = row.year - timeline.codYear;
    if (yearsFromCod < 0 || yearsFromCod >= pathway.revenue.ppaTermYears) return 0;
    return annualMwh / Math.pow(1 + realRate, row.cohortYear);
  }

  const segments = buildPhaseSegments(pathway, flows);

  const phaseBreakdown: PathwayPhaseLcoe[] = segments.map((seg) => {
    let costPv = 0;
    let energyPv = 0;
    let capexUsd = 0;
    for (const row of flows) {
      if (row.year < seg.startYear || row.year > seg.endYear) continue;
      costPv += yearCostPv(row);
      energyPv += yearEnergyPv(row);
      capexUsd += row.capex;
    }
    return {
      phaseId: seg.id,
      label: seg.label,
      classification: pathway.classification,
      powerSource: seg.powerSource,
      lcoeRealUsdPerMwh: energyPv > 0 ? costPv / energyPv : NaN,
      capexUsd,
      years: seg.endYear - seg.startYear + 1,
      startYear: seg.startYear,
      endYear: seg.endYear,
      energyPvMwh: energyPv,
      costPvUsd: costPv,
    };
  });

  // Energy-PV-weighted blend = Σ costPv / Σ energyPv — reconciles to whole.
  const totalCostPv = phaseBreakdown.reduce((s, p) => s + p.costPvUsd, 0);
  const totalEnergyPv = phaseBreakdown.reduce((s, p) => s + p.energyPvMwh, 0);
  const blendedLcoeRealUsdPerMwh = totalEnergyPv > 0 ? totalCostPv / totalEnergyPv : NaN;

  return {
    blendedLcoeRealUsdPerMwh,
    wholePathwayLcoeRealUsdPerMwh: whole.lcoeRealPerMwhUsd,
    phaseBreakdown,
  };
}
